/**
 * ── THE SOCIAL CHANNEL ROWS (audit row 9) ────────────────────────────────────
 * Port of the monolith's `SOC_CH` + `__socPickerHtml` (index.html L11697–11727)
 * and the toggle wiring at L13674–13686.
 *
 * WHY FIVE ROWS AND NOT AN ICON GRID. Five URL boxes were replaced on 2026-08-18
 * (option B, per Jack) by five channel rows showing the FULL url — "make sure it
 * shows the url" — because a division sanity-checks the link, not the logo.
 * Clicking a row leaves that icon out of the email, which is exactly what the
 * block's own guardrail asks for ("remove icon if unused"). A channel the
 * division has no handle for reads "not on file" and is not selectable.
 *
 * TOGGLING OFF HAS TO STICK, AND THAT IS THE WHOLE POINT. ECH_SocialIcons is a
 * DYNAMIC-ONLY block, so `echFillAll` re-writes its five fields on every Step-1
 * change. Clearing the URL alone would last exactly until the next fill. So the
 * toggle also records the field in `__socOff` (the same per-field ownership list
 * a hand-typed URL uses), and `echDynFill` skips it from then on. Turning the
 * channel back ON drops the flag and re-reads the feed in the same patch — the
 * monolith writes the feed value straight back rather than waiting for a fill.
 *
 * PURE ON PURPOSE. The monolith builds an HTML string and mutates `m.data`;
 * React owns both here, so this module returns the ROWS as data and the toggle
 * as a PATCH. Same decisions, same order, no DOM.
 */
import { echDynVal, socOffList, type DynBlock, type EchCtx } from "./echDyn";

export interface SocChannel {
  /** the block's declared field key */
  k: string;
  /** what the row calls it */
  name: string;
  /** the `social.<x>` feed suffix in ECH_DYN */
  feed: string;
}

/** monolith `SOC_CH` (L11697–11698) — order is the order the rows render in. */
export const SOC_CH: readonly SocChannel[] = [
  { k: "facebookUrl", name: "Facebook", feed: "fb" },
  { k: "xUrl", name: "X", feed: "x" },
  { k: "instagramUrl", name: "Instagram", feed: "ig" },
  { k: "pinterestUrl", name: "Pinterest", feed: "pin" },
  { k: "youtubeUrl", name: "YouTube", feed: "yt" },
];

/** monolith L13296 — the picker is ECH_SocialIcons' and no other block's. */
export function socPickerOn(echName: string): boolean {
  return /^ECH_SocialIcons$/i.test(String(echName || ""));
}

/** the five field keys the picker owns, so the field loop can skip them */
export function socKeys(): Record<string, 1> {
  const out: Record<string, 1> = {};
  SOC_CH.forEach((c) => { out[c.k] = 1; });
  return out;
}

export interface SocRow extends SocChannel {
  /** what /sfmc-social-links has for this division (may be "") */
  feedUrl: string;
  /** what is on the block right now */
  cur: string;
  /** in the email as it stands — a usable http(s) value */
  live: boolean;
  /** the division HAS a handle, so the row is selectable */
  has: boolean;
  /** what the row displays and what Copy/Open point at (`cur || feedUrl`) */
  url: string;
}

/**
 * monolith `__socPickerHtml`'s row loop (L11701–11719), verbatim in its
 * decisions: `live` is measured off the CURRENT value (what the email carries),
 * `has` off the FEED (whether the division owns the channel at all), and the
 * displayed url is `cur || feed` — never the feed when a hand-typed value exists.
 */
export function socRows(m: DynBlock, ctx: EchCtx | null | undefined, lvl: string, doc?: DynBlock[]): { rows: SocRow[]; on: number } {
  let on = 0;
  const data = (m && m.data) || {};
  const rows = SOC_CH.map((c) => {
    const feedUrl = ctx ? String(echDynVal("social." + c.feed, lvl, ctx, doc, m) || "") : "";
    const cur = String(data[c.k] || "");
    const live = !!(cur && /^https?:\/\//i.test(cur));
    if (live) on++;
    const has = !!feedUrl;
    return { ...c, feedUrl, cur, live, has, url: has ? (cur || feedUrl) : "" };
  });
  return { rows, on };
}

/**
 * monolith L13674–13686. Off clears the URL **and** flags the field so the next
 * `echDynFill` leaves it alone; on drops the flag and writes the feed value back
 * in the same patch.
 */
export function socTogglePatch(m: DynBlock, k: string, feedUrl: string): Record<string, unknown> {
  const cur = String(((m && m.data) || {})[k] || "");
  if (cur && /^https?:\/\//i.test(cur)) return { [k]: "", __socOff: socOffList(m, k, true) };
  return { [k]: String(feedUrl || ""), __socOff: socOffList(m, k, false) };
}

/** the grey line under the rows (monolith L11723) */
export function socNote(divisionCode: string): string {
  const dv = String(divisionCode || "").toUpperCase();
  return dv ? "From the " + dv + " division." : "Pick a division on Step 1.";
}
